import { existsSync, mkdirSync, writeFileSync } from "fs"
import { join } from "path"
import type { FeatureInstaller, ProjectConfig } from "../../types/types.js"
import { logger } from "../../utils/logger.js"
import { PackageManagerAdapter } from "../../utils/package-manager.js"
import { globalsCssTemplate, postcssConfigTemplate } from "./templates.js"

export class TailwindInstaller implements FeatureInstaller {
  name = "Tailwind CSS v4"

  async install(config: ProjectConfig): Promise<void> {
    logger.info("Setting up Tailwind CSS v4...")

    if (config.dryRun) {
      logger.debug(
        "[DRY RUN] Would install Tailwind, write globals.css and postcss.config.js",
      )
      return
    }

    const pm = new PackageManagerAdapter(config.packageManager)

    await pm.add(
      ["tailwindcss@latest", "@tailwindcss/postcss@latest"],
      true,
      config,
    )

    this.createGlobalsCss(config)
    await this.createPostCSSConfig(config)
    this.updateMainCssImport(config)

    logger.success("Tailwind CSS v4 configured successfully")
  }

  private createGlobalsCss(config: ProjectConfig): void {
    const cssPath = this.getCssPath(config)
    const cssDir = cssPath.substring(0, cssPath.lastIndexOf("/"))

    if (config.dryRun) {
      logger.debug(`[DRY RUN] Would create CSS at: ${cssPath}`)
      return
    }

    try {
      if (cssDir && !existsSync(join(config.projectPath, cssDir))) {
        mkdirSync(join(config.projectPath, cssDir), { recursive: true })
      }
      const content = globalsCssTemplate({
        framework: config.framework,
        metaFramework: config.metaFramework,
      })

      writeFileSync(join(config.projectPath, cssPath), content)
      logger.debug(`Created Tailwind CSS file at: ${cssPath}`)
    } catch (error: any) {
      logger.warning(`Could not create Tailwind CSS file: ${error.message}`)
    }
  }

  private async createPostCSSConfig(config: ProjectConfig): Promise<void> {
    const configPath = join(config.projectPath, "postcss.config.js")

    const mjsConfigPath = join(config.projectPath, "postcss.config.mjs")

    if (config.dryRun) {
      logger.debug("[DRY RUN] Would create PostCSS config")
      return
    }

    try {
      if (existsSync(mjsConfigPath)) {
        const { unlinkSync } = await import("fs")
        unlinkSync(mjsConfigPath)
        logger.debug("Removed existing postcss.config.mjs")
      }

      writeFileSync(configPath, postcssConfigTemplate())
      logger.debug("Created PostCSS config")
    } catch (error: any) {
      logger.warning(`Could not create PostCSS config: ${error.message}`)
    }
  }

  private getCssPath(config: ProjectConfig): string {
    const { framework, metaFramework } = config

    const paths: Record<string, string> = {
      astro: "src/styles/global.css",
      default: "src/styles/globals.css",
      next: "src/app/globals.css",
      nuxt: "assets/css/main.css",
      remix: "app/styles/globals.css",
      sveltekit: "src/app.css",
    }

    return paths[metaFramework] || paths[framework] || paths.default
  }

  private updateMainCssImport(_config: ProjectConfig): void {
    // This would update the main entry file to import the CSS
    // Implementation depends on the framework
    logger.debug("CSS import paths updated")
  }
}
